import { type CheckoutScope } from '@impact/types';

/**
 * Modal → /donate/checkout handoff (sessionStorage).
 *
 * The donation modal collects amount + donor details, then hands off to the
 * Stripe checkout page which creates the PaymentIntent and mounts the Payment
 * Element. Only the random payload key travels in the URL — never PII or a
 * client secret.
 */

const STRIPE_CHECKOUT_STORAGE_PREFIX = 'stripe-checkout';
const PAYLOAD_TTL_MS = 30 * 60 * 1000; // 30 minutes

export interface StripeCheckoutDonor {
  donorEmail: string;
  donorFirstName: string;
  donorLastName: string;
  donorCompanyName?: string;
  donorDisplayName?: string;
  message?: string;
  isAnonymous: boolean;
  giftAidEligible: boolean;
  donorTaxNumber?: string;
}

export interface StripeCheckoutAddress {
  streetAddress: string;
  city: string;
  state?: string;
  postalCode: string;
  country?: string;
  addressLine2?: string;
}

/** Merch line item — itemId/quantity feed the API; name/price are display-only. */
export interface StripeCheckoutItem {
  itemId: string;
  quantity: number;
  name: string;
  price: number;
}

/** Registration completion details the commit call persists for webhook completion. */
export interface StripeCheckoutRegistrationCompletion {
  eventId: string;
  termsAccepted: boolean;
  newsletterOptIn: boolean;
  marketingOptIns?: Record<string, boolean>;
}

export interface StripeCheckoutPayload {
  v: 1;
  createdAt: number;
  scope: CheckoutScope;
  /** Total charge (incl. platform fee) — what the PaymentIntent charges */
  amount: number;
  /** Donation excl. platform fee (display only) */
  baseAmount: number;
  platformFee: number;
  currency: { code: string; symbol: string };
  donor: StripeCheckoutDonor;
  donorCountryCode: string;
  /** Absent when the flow never collected an address (e.g. registration without shipping) */
  donorAddress?: StripeCheckoutAddress;
  /** Who the donation supports — shown in the order summary */
  recipientName: string;
  /** Pathname the donor came from (back/expired links) */
  returnTo: string;
  /** Merch line items (registration checkout) */
  items?: StripeCheckoutItem[];
  /** Present for registration-scope checkouts only */
  registrationCompletion?: StripeCheckoutRegistrationCompletion;
}

export function getStripeCheckoutStorageKey(key: string): string {
  return `${STRIPE_CHECKOUT_STORAGE_PREFIX}:${key}`;
}

function getSessionStorage(): Storage | null {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return window.sessionStorage;
    }

    if (typeof sessionStorage !== 'undefined') {
      return sessionStorage;
    }
  } catch {
    return null;
  }

  return null;
}

/** Save the payload and return the random key to put in the checkout URL. */
export function saveStripeCheckoutPayload(
  payload: Omit<StripeCheckoutPayload, 'v' | 'createdAt'>,
): string | null {
  const storage = getSessionStorage();
  if (!storage) return null;

  try {
    const key = crypto.randomUUID();
    storage.setItem(
      getStripeCheckoutStorageKey(key),
      JSON.stringify({
        ...payload,
        v: 1,
        createdAt: Date.now(),
      } satisfies StripeCheckoutPayload),
    );
    return key;
  } catch {
    // Storage write failures should not block checkout.
    return null;
  }
}

/** Load a payload by key. Returns null when missing, malformed, or expired. */
export function loadStripeCheckoutPayload(key: string): StripeCheckoutPayload | null {
  const storage = getSessionStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(getStripeCheckoutStorageKey(key));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StripeCheckoutPayload>;
    if (
      parsed.v !== 1 ||
      typeof parsed.createdAt !== 'number' ||
      typeof parsed.amount !== 'number' ||
      typeof parsed.scope !== 'object' ||
      parsed.scope === null ||
      typeof parsed.donor !== 'object' ||
      parsed.donor === null
    ) {
      return null;
    }

    if (Date.now() - parsed.createdAt > PAYLOAD_TTL_MS) {
      storage.removeItem(getStripeCheckoutStorageKey(key));
      return null;
    }

    return parsed as StripeCheckoutPayload;
  } catch {
    return null;
  }
}

export function clearStripeCheckoutPayload(key: string): void {
  const storage = getSessionStorage();
  if (!storage) return;

  try {
    storage.removeItem(getStripeCheckoutStorageKey(key));
  } catch {
    // Storage delete failures should not block checkout.
  }
}
